import { useState } from 'react';
import { Link, useMatch, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { ArrowLeft, Check, ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { Skeleton } from '@/components/ui/skeleton';
import { PermissionGate } from '@/shared/components/permission-gate';
import { Permisos } from '@/shared/constants/permisos';
import {
  useAgregarSeccionMutation,
  useEliminarSeccionMutation,
  useReordenarSeccionesMutation,
  useSeccionesTecnologiaQuery,
} from '@/features/recet/queries/tecnologia-seccion.queries';
import { useRecetEditorBridgeStore } from '@/features/recet/store/recet-editor-bridge.store';
import type { TecnologiaSeccionListaDto } from '@/features/recet/types/tecnologia.types';

export function RecetEditorSidebar() {
  // El sidebar se monta desde AdminLayout en cuanto el pathname matchea
  // /admin/recet/tecnologias/<id>, pero la ruta es lazy: hay un instante en
  // que el pathname ya cambio pero el route match aun no esta hidratado.
  // En ese intervalo `useParams({ from: '...' })` (strict) dispara el
  // invariant "Could not find an active match". Esperamos al match con
  // shouldThrow: false antes de invocar los hooks strict.
  const match = useMatch({ from: '/admin/recet/tecnologias/$id', shouldThrow: false });
  if (!match) return null;
  return <RecetEditorSidebarContent />;
}

function RecetEditorSidebarContent() {
  const { id } = useParams({ from: '/admin/recet/tecnologias/$id' });
  const { seccion: activeSeccionId } = useSearch({ from: '/admin/recet/tecnologias/$id' });
  const navigate = useNavigate({ from: '/admin/recet/tecnologias/$id' });
  const tecnologiaId = Number(id) || 0;

  const { data: secciones, isLoading } = useSeccionesTecnologiaQuery(tecnologiaId);
  const agregarMutation = useAgregarSeccionMutation(tecnologiaId);
  const eliminarMutation = useEliminarSeccionMutation(tecnologiaId);
  const reordenarMutation = useReordenarSeccionesMutation(tecnologiaId);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');

  const activas = secciones?.filter(s => s.activo) ?? [];

  function handleSelect(seccionId: number) {
    if (seccionId === activeSeccionId) return;
    useRecetEditorBridgeStore.getState().flushSave?.();
    navigate({ search: { seccion: seccionId } });
  }

  function handleMoveUp(seccion: TecnologiaSeccionListaDto) {
    const idx = activas.findIndex(s => s.id === seccion.id);
    if (idx <= 0) return;
    const newOrder = [...activas];
    const prev = newOrder[idx - 1]!;
    const curr = newOrder[idx]!;
    newOrder[idx - 1] = curr;
    newOrder[idx] = prev;
    reordenarMutation.mutate(
      { orden: newOrder.map(s => s.id) },
      { onError: err => toast.error(err.message) }
    );
  }

  function handleMoveDown(seccion: TecnologiaSeccionListaDto) {
    const idx = activas.findIndex(s => s.id === seccion.id);
    if (idx < 0 || idx >= activas.length - 1) return;
    const newOrder = [...activas];
    const curr = newOrder[idx]!;
    const next = newOrder[idx + 1]!;
    newOrder[idx] = next;
    newOrder[idx + 1] = curr;
    reordenarMutation.mutate(
      { orden: newOrder.map(s => s.id) },
      { onError: err => toast.error(err.message) }
    );
  }

  function handleDelete(seccionId: number) {
    eliminarMutation.mutate(seccionId, {
      onSuccess: () => toast.success('Sección eliminada'),
      onError: err => toast.error(err.message),
    });
  }

  function handleAdd() {
    if (!newName.trim()) return;
    agregarMutation.mutate(
      { nombre: newName.trim() },
      {
        onSuccess: () => {
          toast.success('Sección agregada');
          setNewName('');
          setAddDialogOpen(false);
        },
        onError: err => toast.error(err.message),
      }
    );
  }

  return (
    <Sidebar collapsible="icon" variant="sidebar">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link to="/admin">
                <div className="bg-sidebar-primary flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-lg">
                  <img
                    src="/apeps-isotipo.png"
                    alt="APEPS"
                    className="size-5 object-contain brightness-0 invert"
                  />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="text-sidebar-foreground truncate font-semibold">APEPS</span>
                  <span className="text-sidebar-foreground/50 truncate text-xs">Admin</span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton asChild>
              <Link
                to="/admin/recet/tecnologias"
                className="text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="size-4" />
                <span>Tecnologías</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup data-testid="recet-tecnologia-editor.sidebar-secciones">
          <SidebarGroupLabel>Secciones</SidebarGroupLabel>
          <SidebarMenu className="gap-3 px-1">
            {isLoading ? (
              <div className="space-y-2 px-2 py-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : (
              activas.map((seccion, idx) => {
                const isActive = seccion.id === activeSeccionId;
                const pending =
                  seccion.tieneCambiosPendientes || seccion.tieneComentariosPendientes;
                return (
                  <SidebarMenuItem key={seccion.id} className="group/section">
                    <button
                      type="button"
                      onClick={() => handleSelect(seccion.id)}
                      className={`flex w-full items-center justify-between gap-2.5 rounded-lg px-2 py-2 text-left transition-colors ${
                        isActive ? 'bg-primary/10' : 'hover:bg-sidebar-accent/60'
                      }`}
                    >
                      <span
                        className={`flex size-6 shrink-0 items-center justify-center rounded-md text-xs font-semibold ${
                          isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'
                        }`}
                      >
                        {idx + 1}
                      </span>

                      <span
                        className={`min-w-0 flex-1 text-sm leading-snug break-words whitespace-normal ${
                          isActive ? 'text-primary font-semibold' : 'text-foreground'
                        }`}
                      >
                        {seccion.nombre}
                        {seccion.esPersonalizada && (
                          <Badge
                            variant="outline"
                            className="ml-1.5 px-1 py-0 align-middle text-[9px]"
                          >
                            custom
                          </Badge>
                        )}
                      </span>

                      <span
                        className={`flex size-5 shrink-0 items-center justify-center rounded-full ${
                          pending ? 'bg-warning/20 text-warning' : 'bg-success/20 text-success'
                        }`}
                        aria-hidden
                      >
                        {pending ? (
                          <span className="bg-warning size-2 rounded-full" />
                        ) : (
                          <Check className="size-3" strokeWidth={3} />
                        )}
                      </span>
                    </button>

                    <PermissionGate permiso={Permisos.TECNOLOGIAS_EDITAR}>
                      <div className="bg-sidebar absolute top-1 right-1 flex items-center gap-0.5 rounded-md border opacity-0 shadow-sm transition-opacity group-hover/section:opacity-100">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-5"
                          disabled={idx === 0}
                          onClick={e => {
                            e.stopPropagation();
                            handleMoveUp(seccion);
                          }}
                        >
                          <ChevronUp className="size-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-5"
                          disabled={idx === activas.length - 1}
                          onClick={e => {
                            e.stopPropagation();
                            handleMoveDown(seccion);
                          }}
                        >
                          <ChevronDown className="size-3" />
                        </Button>
                        {seccion.esPersonalizada && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive size-5"
                            onClick={e => {
                              e.stopPropagation();
                              handleDelete(seccion.id);
                            }}
                          >
                            <Trash2 className="size-3" />
                          </Button>
                        )}
                      </div>
                    </PermissionGate>
                  </SidebarMenuItem>
                );
              })
            )}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <PermissionGate permiso={Permisos.TECNOLOGIAS_EDITAR}>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton onClick={() => setAddDialogOpen(true)}>
                <Plus className="size-4" />
                <span>Agregar sección</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </PermissionGate>
      </SidebarFooter>

      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Agregar sección</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Input
              placeholder="Nombre de la sección"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAdd} disabled={agregarMutation.isPending || !newName.trim()}>
              {agregarMutation.isPending ? 'Agregando...' : 'Agregar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Sidebar>
  );
}
